import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { TrendingUp, Gift, Calendar, Zap } from 'lucide-react';

export function RewardsCenter() {
  // Mock rewards data
  const rewardsData = {
    totalEarned: 1247.89,
    claimable: 201.89,
    nextDistribution: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
    compounds: 12,
  };

  const rewardHistory = [
    {
      id: '1',
      type: 'earned',
      amount: 45.67,
      pool: 'NDH Flexible',
      date: new Date(Date.now() - 24 * 60 * 60 * 1000),
      status: 'claimable',
    },
    {
      id: '2',
      type: 'earned',
      amount: 156.23,
      pool: 'NDH 30-Day Lock',
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      status: 'claimable',
    },
    {
      id: '3',
      type: 'claimed',
      amount: 89.45,
      pool: 'NDH Flexible',
      date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      status: 'completed',
    },
    {
      id: '4',
      type: 'compounded',
      amount: 123.78,
      pool: 'NDH 30-Day Lock',
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      status: 'completed',
    },
  ];

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'claimable':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Claimable</Badge>;
      case 'completed':
        return <Badge variant="outline">Completed</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'earned':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'claimed':
        return <Gift className="h-4 w-4 text-blue-600" />;
      case 'compounded':
        return <Zap className="h-4 w-4 text-purple-600" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Rewards Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earned</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rewardsData.totalEarned.toFixed(2)} NDH</div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              ≈ ${(rewardsData.totalEarned * 0.89).toFixed(2)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available to Claim</CardTitle>
            <Gift className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{rewardsData.claimable.toFixed(2)} NDH</div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              ≈ ${(rewardsData.claimable * 0.89).toFixed(2)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Next Distribution</CardTitle>
            <Calendar className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2 days</div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {formatDate(rewardsData.nextDistribution)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Auto-Compounds</CardTitle>
            <Zap className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rewardsData.compounds}</div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              This month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button className="flex-1 min-w-48">
              <Gift className="mr-2 h-4 w-4" />
              Claim All Rewards ({rewardsData.claimable.toFixed(2)} NDH)
            </Button>
            <Button variant="outline" className="flex-1 min-w-48">
              <Zap className="mr-2 h-4 w-4" />
              Auto-Compound All
            </Button>
            <Button variant="secondary" className="flex-1 min-w-48">
              <TrendingUp className="mr-2 h-4 w-4" />
              Reinvest Rewards
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Rewards History */}
      <Card>
        <CardHeader>
          <CardTitle>Rewards History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {rewardHistory.map((reward) => (
              <div key={reward.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    {getTypeIcon(reward.type)}
                  </div>
                  <div>
                    <div className="font-medium capitalize">{reward.type}</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {reward.pool} • {formatDate(reward.date)}
                    </div>
                  </div>
                </div>
                
                <div className="text-right flex items-center gap-3">
                  <div>
                    <div className="font-medium">
                      {reward.type === 'claimed' ? '-' : '+'}{reward.amount.toFixed(2)} NDH
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      ≈ ${(reward.amount * 0.89).toFixed(2)}
                    </div>
                  </div>
                  {getStatusBadge(reward.status)}
                  {reward.status === 'claimable' && (
                    <Button size="sm" variant="outline">
                      Claim
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 text-center">
            <Button variant="outline">
              Load More History
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}